import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { LimeDatePicker } from "@/Components/NextBase/LimeDatePicker";
import { api } from "@/lib/api-client";
import type { GetTimeOffTypes } from "@/server-types";
import {
  toDateInputValue,
  todayInputValue,
} from "@/pages/Dashboard/Users/staffEmployment";
import { CompensationRateFields } from "./CompensationRateFields";
import {
  type CompensationDraft,
  draftFromRate,
  formatCompensation,
  rateFromDraft,
} from "./timeOffCompensation";

type TypeCompensation = NonNullable<
  GetTimeOffTypes["response"][number]["compensation"]
>;

function formatDay(value: Date | string): string {
  const day = toDateInputValue(value);
  return day
    ? new Date(`${day}T12:00:00`).toLocaleDateString(i18n.locale || undefined)
    : "";
}

/**
 * Saved on its own, not with the type: a rate is a dated period, not a field of
 * the type. Shows what each save returns, so the list behind the modal (and the
 * unsaved type fields in it) are not reloaded mid-edit.
 */
export const TimeOffTypeCompensationSection = ({
  typeId,
  initialCompensation,
  onChanged,
}: {
  typeId: number;
  initialCompensation: TypeCompensation;
  onChanged: () => void;
}) => {
  const [compensation, setCompensation] = useState(initialCompensation);
  const [draft, setDraft] = useState<CompensationDraft>(() =>
    draftFromRate(compensation.current),
  );
  const [effectiveFrom, setEffectiveFrom] = useState(todayInputValue());
  const [error, setError] = useState<string | null>(null);

  const postRate = api.timeOffType.usePostTimeOffTypeRate();
  const deleteRate = api.timeOffType.useDeleteTimeOffTypeRate();

  async function save() {
    const rate = rateFromDraft(draft);
    if (!rate) {
      setError(
        draft.mode === "percentOfHourlyCost"
          ? t`Vnesite odstotek med 0 in 100`
          : t`Vnesite znesek`,
      );
      return;
    }
    setError(null);
    setCompensation(
      await postRate.mutateAsync({
        id: typeId,
        body: { ...rate, effectiveFrom },
      }),
    );
    onChanged();
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-lg bg-default-50 p-3"
      data-identifier="time-off-type-compensation"
    >
      <div>
        <p className="font-medium text-sm">
          <Trans>Nadomestilo</Trans>
        </p>
        <p className="text-default-500 text-xs">
          <Trans>
            Kako se ta odsotnost upošteva v strošku dela. Ocena na podlagi
            urnega stroška zaposlenega, ne obračun plače.
          </Trans>
        </p>
      </div>

      <p className="text-sm">
        {compensation.current ? (
          <Trans>Trenutno: {formatCompensation(compensation.current)}</Trans>
        ) : (
          <Trans>Trenutno: ni nastavljeno, šteje kot delovni čas</Trans>
        )}
      </p>

      {compensation.history.length > 0 && (
        <div className="flex flex-col gap-1">
          {compensation.history.map((rate) => (
            <div
              key={rate.id}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <span>
                {formatDay(rate.effectiveFrom)} –{" "}
                {rate.effectiveTo ? formatDay(rate.effectiveTo) : t`danes`}
              </span>
              <span className="flex items-center gap-2">
                {formatCompensation(rate)}
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label={t`Izbriši obdobje`}
                  isLoading={deleteRate.isPending}
                  onPress={async () => {
                    setCompensation(
                      await deleteRate.mutateAsync({
                        id: typeId,
                        rateId: rate.id,
                      }),
                    );
                    onChanged();
                  }}
                >
                  <Trash2 size={14} />
                </Button>
              </span>
            </div>
          ))}
        </div>
      )}

      <CompensationRateFields draft={draft} onChange={setDraft} error={error}>
        <LimeDatePicker
          label={t`Velja od`}
          showMonthAndYearPickers
          value={parseDate(effectiveFrom)}
          onChange={(value) => value && setEffectiveFrom(value.toString())}
        />
      </CompensationRateFields>

      <div className="flex justify-end">
        <Button
          size="sm"
          color="primary"
          variant="flat"
          onPress={() => save().catch(() => {})}
          isLoading={postRate.isPending}
          data-identifier="time-off-type-compensation-save"
        >
          <Trans>Shrani nadomestilo</Trans>
        </Button>
      </div>
    </div>
  );
};
